import React from "react";
import {
  Button,
  Flex,
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuTrigger,
  Portal,
  Text,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FiChevronRight, FiX } from "react-icons/fi";
import { UserIcon } from "@/components/Icons";
import { useUser } from "@/contexts/UserContext";
import { useSignOut } from "@/apis/hooks/auth/signing";
import { NOTIFICATION_SETTINGS_SCREEN_URL } from "@/constants/urls";
import Loading from "@/components/Loading";

const ITEM_STYLE = {
  h: "40px",
  px: 4,
  py: 2,
  cursor: "pointer",
  bg: "white",
  borderRadius: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  // Hover / pointer-highlight: grey background only, no ring.
  _hover: { bg: "textBlack.200" },
  _highlighted: { bg: "textBlack.200" },
  // Focus (click or keyboard — NOT hover) and active press: magenta ring.
  _focus: {
    bg: "textBlack.200",
    outline: "none",
    boxShadow: "inset 0 0 0 4px var(--chakra-colors-focus-500)",
    animation: "menu-item-focus-pulse 300ms ease-in-out",
  },
  _active: {
    boxShadow: "inset 0 0 0 4px var(--chakra-colors-focus-500)",
    animation: "menu-item-focus-pulse 300ms ease-in-out",
  },
};

export default function UserMenu() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { mutate: signOut, isPending: isSignOutLoading } = useSignOut();

  return (
    <>
      {isSignOutLoading && (
        <Portal>
          <Loading />
        </Portal>
      )}
      <MenuRoot positioning={{ placement: "bottom-end" }} position="relative">
        <MenuTrigger asChild>
          <Button
            variant="plain"
            p={0}
            h="auto"
            minW={0}
            outline="none"
            className="focus-bordered"
            borderRadius="2px"
            aria-label={
              [
                [user?.first_name, user?.last_name].filter(Boolean).join(" "),
                "Օգտատիրոջ ընտրացանկ",
              ]
                .filter(Boolean)
                .join(" — ")
            }
            _open={{
              boxShadow: "0 0 0 2px transparent, 0 0 0 4px var(--chakra-colors-focus-500)",
            }}
          >
            <Flex alignItems="center" gap={2}>
              <Text color="white" textStyle="body.regular" display={{ base: "none", md: "block" }}>
                {user?.first_name} {user?.last_name}
              </Text>
              <UserIcon w="26px" h="26px" aria-hidden="true" />
            </Flex>
          </Button>
        </MenuTrigger>
        <MenuContent
          w="280px"
          mt={3}
          p={0}
          bg="white"
          borderRadius="4px"
          borderWidth="2px"
          borderStyle="solid"
          borderColor="primary.300"
          boxShadow="0 4px 8px 0 rgba(0, 0, 0, 0.3)"
          overflow="visible"
          position="absolute"
          right={{ base: -4, md: 10 }}
          top={10}
          _before={{
            content: '""',
            position: "absolute",
            top: "-10px",
            right: "18px",
            w: 0,
            h: 0,
            borderLeft: "10px solid transparent",
            borderRight: "10px solid transparent",
            borderBottom: "10px solid #CBD6E3",
          }}
          _after={{
            content: '""',
            position: "absolute",
            top: "-7px",
            right: "20px",
            w: 0,
            h: 0,
            borderLeft: "8px solid transparent",
            borderRight: "8px solid transparent",
            borderBottom: "8px solid white",
          }}
        >
          <MenuItem
            {...ITEM_STYLE}
            value="settings"
            onClick={() => navigate(NOTIFICATION_SETTINGS_SCREEN_URL)}
          >
            <Text textStyle="body.semibold" color="textBlack.800">
              Կարգավորումներ
            </Text>
            <FiChevronRight size={20} color="#1A1A1A" />
          </MenuItem>

          <MenuItem {...ITEM_STYLE} value="log-out" onClick={() => signOut()}>
            <Text textStyle="body.semibold" color="error.500">
              Ելք
            </Text>
            <FiX size={20} color="#CC0000" />
          </MenuItem>
        </MenuContent>
      </MenuRoot>
    </>
  );
}
